import { useCallback, useRef, useState } from "react";
import { ModalShell } from "../modals/ModalShell";
import { LucideIcon } from "./LucideIcon";
import { useAccountLoginUrl } from "../../hooks/useAccountLoginUrl";
import "./referralBand.css";

/**
 * "Invite a friend" dialog opened from the refer-a-friend band. Follows
 * SharePlanModal's copy/share mechanics: tap-to-select URL field, primary
 * "Copy link" with inline feedback (clipboard-API failure falls back to
 * selecting the field), and a native share sheet button where supported.
 * No minting — the link is already resolved by the band.
 */

const COPY_FEEDBACK_MS = 1500;

export type InviteVariant = "reward" | "pending" | "anon";

type Props = {
  variant: InviteVariant;
  /** Absolute URL to share — Yotpo referral link, or the storefront origin. */
  shareUrl: string;
  /** Referral code shown above the link (reward variant only). */
  code: string | null;
  onClose: () => void;
};

const LEDE: Record<InviteVariant, string> = {
  reward:
    "Send your invite link to a friend — you'll each get $10 off when they place their first order.",
  pending:
    "Your referral code isn't ready yet — you can still share Fuel Goods below, and check back soon for rewards.",
  anon:
    "Share Fuel Goods with a friend below — and log in to join Team Fuel Goods to earn referral rewards."
};

export function InviteFriendModal({ variant, shareUrl, code, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const loginUrl = useAccountLoginUrl();

  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    } catch {
      // Clipboard unavailable (insecure context / permission). Select the
      // input so the user can copy manually — the URL is already visible.
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [shareUrl]);

  const handleNativeShare = useCallback(async () => {
    try {
      await navigator.share({
        title: "Fuel Goods",
        text: "Fuel your training with Fuel Goods",
        url: shareUrl
      });
    } catch {
      // User cancelled or share API rejected — no-op. They still have the
      // copy-link affordance.
    }
  }, [shareUrl]);

  return (
    <ModalShell
      eyebrow="Refer a friend"
      title="Invite a friend"
      onClose={onClose}
      width={520}
      footer={
        <>
          <button type="button" className="fg-btn fg-btn--ghost" onClick={onClose}>
            Done
          </button>
          <div className="fgrf-invite__footer-right">
            {canNativeShare && (
              <button
                type="button"
                className="fg-btn fg-btn--ghost"
                onClick={() => void handleNativeShare()}>
                Share…
              </button>
            )}
            <button
              type="button"
              className="fg-btn fg-btn--primary"
              onClick={() => void handleCopy()}>
              {copied ? (
                <>
                  Copied <LucideIcon name="check" size={16} />
                </>
              ) : (
                "Copy link"
              )}
            </button>
          </div>
        </>
      }>
      <div className="fgp-modalshell__pad fgrf-invite">
        <p className="fgrf-invite__lede">{LEDE[variant]}</p>
        {variant === "reward" && code && (
          <p className="fgrf-invite__codeline">
            Your code · <strong>{code}</strong>
          </p>
        )}
        <label className="fgrf-invite__url-label" htmlFor="fgrf-invite-url">
          Your invite link
        </label>
        <input
          ref={inputRef}
          id="fgrf-invite-url"
          className="fg-input fgrf-invite__url"
          readOnly
          value={shareUrl}
          onFocus={(e) => e.currentTarget.select()}
        />
        {variant === "anon" && (
          <a className="fgrf-invite__login" href={loginUrl}>
            Log in to join Team Fuel Goods
          </a>
        )}
        <p aria-live="polite" className="fgrf-invite__sr-status">
          {copied ? "Link copied to clipboard" : ""}
        </p>
      </div>
    </ModalShell>
  );
}
